import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetIccidTodayUsageDTO } from 'src/app/core/models/system/widget/widget-Iccid-today-usage.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  selector: 'app-chart-todays-usage',
  templateUrl: './chart-todays-usage.component.html',
  styleUrls: ['./chart-todays-usage.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartTodaysUsageComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  
  @Output() eSimManagerFormCancelled: EventEmitter<void>=new EventEmitter<void>();
  
  @ViewChild('chartRef') chartRef!: ChartComponent;
  
  get todaysUsageGraphInMbOnly() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.FormTimelineTodaysUsageGraphInMbOnly, false); }
  
  private buildIccidTodayUsageChart: boolean=true;
  private subIccidTodayUsage!      : Subscription | undefined;
  private todayUsageTotal          : number       | undefined;
  private todayUsageTotalSymbol    : string | undefined;
  private todayUsageValues         : { x: number, y: number }[]=[];
  
  public expandContainer : boolean=true;
  public iccid           : string | undefined;
  public todayUsageChart : any    | undefined;
  public visibleContainer: boolean=true;

  constructor(
    private chartConfigService : ChartConfigService,
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=250;

      if (this.chartTitle==undefined)
        this.chartTitle="Todays Usage";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildIccidTodayUsageChart=true;
      this.todayUsageTotal          =undefined;
      this.todayUsageValues         =[];
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.unsubscribeIccidTodayUsage();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public getIccidTodayUsage(iccid: string | undefined) {
    try {
      if (iccid==undefined)
        throw new Error("ChartTodaysUsageComponent - ICCID is undefined");

      this.iccid=iccid;

      this.unsubscribeIccidTodayUsage();
      this.subIccidTodayUsage=new Subscription();

      //CRN_20261013 - Retired
      // const todayUsageSubscription=this.widgetService.getIccidTodayUsage(this.iccid)
      // .pipe(
      //   catchError(error =>
      //     throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
      //   )
      // )
      // .subscribe({
      //   next : data => this.updateTodayUsageChart(data),
      //   error: error => this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)))
      // });
      //
      // this.subIccidTodayUsage.add(todayUsageSubscription);

      const todayUsageUpdatesSubscription=this.widgetService.getIccidTodayUsageUpdates(this.iccid)
        .pipe(
          catchError(error =>
            throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
          )
        )
        .subscribe({
          next : data => this.updateTodayUsageChart(data),
          error: error => this.exceptionsService.handleException(new Error(error))
        });

      this.subIccidTodayUsage.add(todayUsageUpdatesSubscription);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public addReading(data: WidgetIccidTodayUsageDTO): void {
    try {
      this.updateTodayUsageChart(data);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public resetReadings(): void {
    try {
      this.unsubscribeIccidTodayUsage();

      this.buildIccidTodayUsageChart=true;
      this.todayUsageChart          =undefined;
      this.todayUsageTotal          =undefined;
      this.todayUsageValues         =[];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private updateTodayUsageChart(data: WidgetIccidTodayUsageDTO) {
    try {
      if (typeof data?.DayUsage!=='number')
        return;

      const size=data.DayUsage;

      if (this.todaysUsageGraphInMbOnly) {
        this.todayUsageTotalSymbol="MB";
        this.todayUsageTotal      =size/(1024*1024);

      } else if (size>=1024*1024*1024) {
        this.todayUsageTotalSymbol="GB";
        this.todayUsageTotal      =size/(1024*1024*1024);

      } else if (size>=1024*1024) {
        this.todayUsageTotalSymbol="MB";
        this.todayUsageTotal      =size/(1024*1024);

      } else if (size>=1024) {
        this.todayUsageTotalSymbol="KB";
        this.todayUsageTotal      =size/1024;

      } else {
        this.todayUsageTotalSymbol="B";
        this.todayUsageTotal      =size;
      }
  
      const now          =new Date().getTime();
      var localtimeoffset=0 - ((new Date().getTimezoneOffset()) * 60000);
      const newPoint     ={ x: now + localtimeoffset, y: Number(this.todayUsageTotal.toFixed(1)) };
      this.todayUsageValues.push(newPoint);
  
      const maxPoints=300;

      if (this.todayUsageValues.length>maxPoints) {
        this.todayUsageValues=this.todayUsageValues.slice(-maxPoints);
      }
  
      if (this.buildIccidTodayUsageChart) {
        this.buildIccidTodayUsageChart=false;
        this.iccidUsageForCurrentDayChart('["--vz-danger", "--vz-success"]');
      }
  
      this.chartRef?.updateSeries([
        {
          name: 'Usage ' + this.todayUsageTotalSymbol,
          data: this.todayUsageValues,
          type: 'line'
        }
      ], true);
    } catch (e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
      
  public unsubscribeIccidTodayUsage() {
    try {
      if (this.subIccidTodayUsage) {
        this.subIccidTodayUsage.unsubscribe();
        this.subIccidTodayUsage=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  private iccidUsageForCurrentDayChart(colors: any) {
    try {
      colors=this.chartConfigService.resolveColours(colors);
           
      this.todayUsageChart={
        chart: {
          id     : 'iccid-today-usage-chart',
          height : this.chartHeight,
          pan: {
            enabled: false
          },
          toolbar: {
              show: false,
          },
          type: 'line',
          zoom: {
            enabled: false
          },
        },
        colors     : colors,
        plotOptions: {
          bar: {
            columnWidth: '90%'
          }
        },
        series: [{
          name: 'Usage ' + this.todayUsageTotalSymbol,
          data: [...this.todayUsageValues],
          type: 'line'
        }],
        stroke: {
          curve: 'smooth',
          width: 3
        },
        title: {
          style: {
            fontWeight: 600,
          },
          text: undefined
        },
        xaxis: {
          type      : 'datetime',
          tickAmount: 10,
          labels    : {
            show             : true,
            rotate           : -45,
            datetimeFormatter: {
              hour  : 'HH:mm:ss',
              minute: 'HH:mm:ss',
              second: 'HH:mm:ss'
            }
          },
          tooltip: {
            enabled: true
          }
        },
        yaxis: [{
          title : {
            text: 'Usage (MB)',
            style: {
              fontWeight: 600,
              fontSize  : '12px'
            },
            offsetX: 0,
            offsetY: 0,
            rotate : 270,
          },
          labels: {
            show    : true,
            minWidth: 15,
            style: {
              fontSize: '12px'
            },
            formatter: (value: number) => Math.round(value).toString()
          }
        }],
        legend: {
          show           : true,
          position       : 'bottom',
          horizontalAlign: 'center',
          fontSize       : '12px',
          labels: {
            colors         : '#666',
            useSeriesColors: true
          }
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
